import React from 'react';
import Typography, {
  TypographyVariant,
} from '../../components/typography/typography';
import Card from '../../components/card/card';
import CardContent from '../../components/card/card-content';
import CardHeader from '../../components/card/card-header';
import Paper from '../../components/paper/paper';
import Divider from '../../components/divider/divider';
import { RuleOfThumbInputsData } from './rule-of-thumb-inputs';

interface RuleOfThumbResultsProps {
  inputData: RuleOfThumbInputsData | null;
}

interface ExpenseBreakdown {
  name: string;
  monthlyAmount: number;
  yearlyAmount: number;
  description: string;
}

interface CalculationResults {
  breakEvenRentPercentage: number;
  breakEvenRentPercentageMonthly: number;
  monthlyBreakEvenRent: number;
  yearlyBreakEvenRent: number;
  expenses: ExpenseBreakdown[];
  totalMonthlyExpenses: number;
  totalYearlyExpenses: number;
}

function calculateBreakEvenRent(
  data: RuleOfThumbInputsData
): CalculationResults {
  const {
    propertyValue,
    downPaymentPercent,
    interestRate,
    propertyTaxPercent,
    repairsPercent,
    landlordInsurancePercent,
    vacancyRate,
    propertyManagementPercent,
  } = data;

  // Calculate loan details
  const downPaymentAmount = propertyValue * (downPaymentPercent / 100);
  const loanAmount = propertyValue - downPaymentAmount;
  const monthlyInterestRate = interestRate / 100 / 12;
  const numberOfPayments = 30 * 12; // 30 year loan

  // Calculate monthly mortgage payment (Principal & Interest)
  const monthlyMortgagePayment =
    (loanAmount *
      (monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);

  // Calculate other monthly expenses
  const monthlyPropertyTax = (propertyValue * (propertyTaxPercent / 100)) / 12;
  const monthlyRepairs = (propertyValue * (repairsPercent / 100)) / 12;
  const monthlyInsurance =
    (propertyValue * (landlordInsurancePercent / 100)) / 12;

  // Calculate base monthly expenses (before vacancy and management)
  const baseMonthlyExpenses =
    monthlyMortgagePayment +
    monthlyPropertyTax +
    monthlyRepairs +
    monthlyInsurance;

  // To account for vacancy and management fees, we need to solve:
  // rent * (1 - vacancyRate/100) * (1 - propertyManagementPercent/100) = baseMonthlyExpenses
  const effectiveRentMultiplier =
    (1 - vacancyRate / 100) * (1 - propertyManagementPercent / 100);
  const monthlyBreakEvenRent = baseMonthlyExpenses / effectiveRentMultiplier;

  // Calculate actual vacancy and management costs based on break-even rent
  const monthlyVacancyCost = monthlyBreakEvenRent * (vacancyRate / 100);
  const monthlyManagementCost =
    monthlyBreakEvenRent * (propertyManagementPercent / 100);

  const totalMonthlyExpenses =
    baseMonthlyExpenses + monthlyVacancyCost + monthlyManagementCost;
  const yearlyBreakEvenRent = monthlyBreakEvenRent * 12;
  const breakEvenRentPercentage =
    ((monthlyBreakEvenRent * 12) / propertyValue) * 100;
  const breakEvenRentPercentageMonthly =
    (monthlyBreakEvenRent / propertyValue) * 100;

  const expenses: ExpenseBreakdown[] = [
    {
      name: 'Mortgage Payment (P&I)',
      monthlyAmount: monthlyMortgagePayment,
      yearlyAmount: monthlyMortgagePayment * 12,
      description: 'Principal and Interest',
    },
    {
      name: 'Property Tax',
      monthlyAmount: monthlyPropertyTax,
      yearlyAmount: monthlyPropertyTax * 12,
      description: `${propertyTaxPercent}% of property value`,
    },
    {
      name: 'Repairs & Maintenance',
      monthlyAmount: monthlyRepairs,
      yearlyAmount: monthlyRepairs * 12,
      description: `${repairsPercent}% of property value`,
    },
    {
      name: 'Landlord Insurance',
      monthlyAmount: monthlyInsurance,
      yearlyAmount: monthlyInsurance * 12,
      description: `${landlordInsurancePercent}% of property value`,
    },
    {
      name: 'Vacancy Allowance',
      monthlyAmount: monthlyVacancyCost,
      yearlyAmount: monthlyVacancyCost * 12,
      description: `${vacancyRate}% of rental income`,
    },
    {
      name: 'Property Management',
      monthlyAmount: monthlyManagementCost,
      yearlyAmount: monthlyManagementCost * 12,
      description: `${propertyManagementPercent}% of rental income`,
    },
  ];

  return {
    breakEvenRentPercentage,
    breakEvenRentPercentageMonthly,
    monthlyBreakEvenRent,
    yearlyBreakEvenRent,
    expenses,
    totalMonthlyExpenses,
    totalYearlyExpenses: totalMonthlyExpenses * 12,
  };
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercentage(percentage: number): string {
  return `${percentage.toFixed(2)}%`;
}

function RuleOfThumbResults({ inputData }: RuleOfThumbResultsProps) {
  if (!inputData) {
    return (
      <Paper
        elevation={3}
        style={{
          height: '100%',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
        className="bg-dark-secondary"
      >
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Typography
            variant={TypographyVariant.H6}
            className="text-primary mb-4"
          >
            Break-Even Analysis
          </Typography>
          <Typography
            variant={TypographyVariant.Subtitle2}
            className="text-secondary"
          >
            Enter property details to see break-even rent calculation
          </Typography>
        </div>
      </Paper>
    );
  }

  const results = calculateBreakEvenRent(inputData);

  return (
    <Paper
      elevation={3}
      style={{
        height: 'calc(100vh - 240px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        overflowY: 'auto',
      }}
      className="bg-dark-secondary"
    >
      <Card
        style={{
          boxShadow: 'none',
          borderRadius: '0px',
          border: 'none',
          height: '100%',
        }}
        className="bg-dark-secondary"
      >
        <CardHeader
          title="Break-Even Analysis"
          className="text-primary"
          style={{ padding: '12px 20px 6px 20px' }}
        />

        <CardContent
          style={{
            padding: '0 20px 40px 20px',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 300px)',
          }}
        >
          {/* Main Result */}
          <div
            style={{
              padding: '20px',
              borderRadius: '8px',
              background:
                'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              marginBottom: '20px',
            }}
          >
            <Typography
              variant={TypographyVariant.Subtitle2}
              className="text-secondary mb-2"
              style={{ textAlign: 'center' }}
            >
              Break-Even Rent Percentage
            </Typography>

            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Monthly Percentage - Primary (Centered) */}
              <div style={{ textAlign: 'center' }}>
                <Typography
                  variant={TypographyVariant.H2}
                  className="text-primary"
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    margin: '4px 0',
                  }}
                >
                  {formatPercentage(results.breakEvenRentPercentageMonthly)}
                </Typography>
                <Typography
                  variant={TypographyVariant.Subtitle2}
                  className="text-secondary"
                  style={{ fontSize: '0.9rem' }}
                >
                  per month
                </Typography>
              </div>

              {/* Yearly Percentage - Secondary (Right aligned) */}
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant={TypographyVariant.H5}
                  className="text-primary"
                  style={{ fontWeight: 600, margin: '2px 0' }}
                >
                  {formatPercentage(results.breakEvenRentPercentage)}
                </Typography>
                <Typography
                  variant={TypographyVariant.Subtitle2}
                  className="text-secondary"
                  style={{ fontSize: '0.8rem' }}
                >
                  per year
                </Typography>
              </div>
            </div>
          </div>

          {/* Monthly/Yearly Rent */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
            <div
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography
                variant={TypographyVariant.Subtitle2}
                className="text-secondary"
                style={{ fontSize: '0.85rem' }}
              >
                Monthly Rent Needed
              </Typography>
              <Typography
                variant={TypographyVariant.H6}
                className="text-primary"
                style={{ fontWeight: 600, margin: '2px 0' }}
              >
                {formatCurrency(results.monthlyBreakEvenRent)}
              </Typography>
            </div>
            <div
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography
                variant={TypographyVariant.Subtitle2}
                className="text-secondary"
                style={{ fontSize: '0.85rem' }}
              >
                Yearly Rent Needed
              </Typography>
              <Typography
                variant={TypographyVariant.H6}
                className="text-primary"
                style={{ fontWeight: 600, margin: '2px 0' }}
              >
                {formatCurrency(results.yearlyBreakEvenRent)}
              </Typography>
            </div>
          </div>

          <Divider style={{ margin: '18px 0' }} />

          {/* Expense Breakdown */}
          <Typography
            variant={TypographyVariant.H6}
            className="text-primary mb-2"
          >
            Monthly Expense Breakdown
          </Typography>

          <div style={{ marginBottom: '12px' }}>
            {results.expenses.map((expense, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom:
                    index < results.expenses.length - 1
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : 'none',
                }}
              >
                <div style={{ flex: 1, paddingRight: '12px' }}>
                  <Typography
                    variant={TypographyVariant.Subtitle2}
                    className="text-primary"
                    style={{ fontSize: '0.9rem' }}
                  >
                    {expense.name}
                  </Typography>
                  <Typography
                    variant={TypographyVariant.Subtitle2}
                    className="text-secondary"
                    style={{ fontSize: '0.75rem' }}
                  >
                    {expense.description}
                  </Typography>
                </div>
                <div style={{ textAlign: 'right', minWidth: '90px' }}>
                  <Typography
                    variant={TypographyVariant.Subtitle2}
                    className="text-primary"
                    style={{ fontWeight: 600, fontSize: '0.9rem' }}
                  >
                    {formatCurrency(expense.monthlyAmount)}
                  </Typography>
                  <Typography
                    variant={TypographyVariant.Subtitle2}
                    className="text-secondary"
                    style={{ fontSize: '0.75rem' }}
                  >
                    {formatCurrency(expense.yearlyAmount)}/yr
                  </Typography>
                </div>
              </div>
            ))}
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* Total */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px',
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography
              variant={TypographyVariant.H6}
              className="text-primary"
              style={{ fontSize: '1rem' }}
            >
              Total Monthly Expenses
            </Typography>
            <div style={{ textAlign: 'right' }}>
              <Typography
                variant={TypographyVariant.H6}
                className="text-primary"
                style={{ fontWeight: 600 }}
              >
                {formatCurrency(results.totalMonthlyExpenses)}
              </Typography>
              <Typography
                variant={TypographyVariant.Subtitle2}
                className="text-secondary"
                style={{ fontSize: '0.8rem' }}
              >
                {formatCurrency(results.totalYearlyExpenses)}/year
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </Paper>
  );
}

export default RuleOfThumbResults;
